import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  buffer,
  debounceTime,
  combineLatest,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  NotificationPreferences,
  NotificationPreferencesStateService,
} from '@usf/ngrx-alerts';
import { ActivatedRoute, Params } from '@angular/router';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { CustomerStoreService } from '@app/ngrx-customer/services/customer/customer-store.service';
import {
  AlertDetail,
  AlertType,
  SaveNotificationPreferencesRequest,
  UserPreferencesCustomerInfo,
} from '@usf/alert-types/user-notification-preference-api';
import { Customer } from '@usf/customer-types/customer';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PendingNotificationPreferencesService } from '../services/pending-notfication-preferences.service';
import { PanAppState, PlatformService } from '@panamax/app-state';

interface GroupCustomersPageViewModel {
  userId: string;
  email: string;
  activeTypeId: string;
  activeGroupId: string;
  activeGroupName?: string;
  breadcrumbs: Crumb[];
  notificationGroupIds: { name: string; id: string }[];
  sortToggle: boolean;
  customerFilter: string;
  customerCount: number;
  enabledCustomerCount: { [groupIds: string]: number };
  customers: { [groupIds: string]: ViewModelCustomer[] };
}

interface ViewModelCustomer {
  customerNumber: string;
  divisionNumber: string;
  customerName: string;
  isEnabled: boolean;
}

@Component({
  selector: 'app-notification-preferences-group-customers',
  templateUrl: './notification-preferences-group-customers.page.html',
  styleUrls: ['./notification-preferences-group-customers.page.scss'],
  standalone: false,
})
export class NotificationPreferencesGroupCustomersPage
  implements OnInit, ViewWillEnter, ViewWillLeave
{
  static readonly TYPE_ID_PARAM = 'typeId';
  static readonly GROUP_ID_PARAM = 'groupId';
  static readonly TYPE_ID_BACK_LINK_MAP: Record<string, string> = {
    DELIVERY: 'i18n.notifications.preferences.DELIVERY_BACK_LINK',
    DIRECT: 'i18n.notifications.preferences.DIRECT_BACK_LINK',
    ORDER: 'i18n.notifications.preferences.ORDER_BACK_LINK',
    PAYMENT: 'i18n.notifications.preferences.PAYMENT_BACK_LINK',
    TM_NOTE: 'i18n.notifications.preferences.TM_NOTE_BACK_LINK',
    SERVICE_REQUEST: 'i18n.notifications.preferences.SERVICE_REQUEST_BACK_LINK',
  };
  static readonly EMPTY_VIEW_MODEL: GroupCustomersPageViewModel = {
    userId: '',
    email: '',
    activeTypeId: '',
    activeGroupId: '',
    breadcrumbs: [],
    notificationGroupIds: [],
    sortToggle: false,
    customerFilter: '',
    customerCount: 0,
    enabledCustomerCount: {},
    customers: {},
  };

  public isMobile = false;
  public customersPageViewModel$: Observable<GroupCustomersPageViewModel> =
    EMPTY;
  private readonly viewModelSubject: BehaviorSubject<GroupCustomersPageViewModel> =
    new BehaviorSubject<GroupCustomersPageViewModel>(
      NotificationPreferencesGroupCustomersPage.EMPTY_VIEW_MODEL,
    );
  private readonly pageInViewSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  private readonly latestNotificationPreferencesSubject: BehaviorSubject<
    NotificationPreferences | undefined
  > = new BehaviorSubject<NotificationPreferences | undefined>(undefined);
  private readonly staleSubject: Subject<void> = new Subject();
  private customers: Customer[] = [];

  // Observable references for better performance and clarity
  private readonly latestNotificationPreferences$ =
    this.latestNotificationPreferencesSubject.asObservable();
  private readonly stale$ = this.staleSubject.asObservable();

  // A map of customer numbers to their latest state (enabled or disabled)
  private readonly latestStates = new Map<string, 'enabled' | 'disabled'>();
  private readonly toggleSubject = new Subject<{
    customerNumber: string;
    divisionNumber: string;
    state: 'enabled' | 'disabled';
  }>();

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly route: ActivatedRoute,
    private readonly platformService: PlatformService,
    private readonly panAppState: PanAppState,
    private readonly notificationPreferencesStateService: NotificationPreferencesStateService,
    private readonly customerStoreService: CustomerStoreService,
    private readonly pendingNotificationPreferencesService: PendingNotificationPreferencesService,
  ) {
    this.destroyRef = destroyRef;
    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;
    this.customersPageViewModel$ = this.viewModelSubject.asObservable();
    this.notificationPreferencesStateService.loadNotificationPreferences();
  }

  /**
   * Initialize the page
   * - The pageViewSubject mechanism is a simple way to turn subscriptions on and off when a
   *   page is in view. Otherwise, Ionic will keep subscriptions running while a cached
   *   page is not in view.
   */
  ngOnInit(): void {
    /**
     *  This subscriotion watches the latestNotificationPreferencesSubject and synchronizes
     *  the View Model object. The updated NotificationPreference object can be rom the
     * backend or temporary changes made by the user.
     */
    this.pageInViewSubject
      .pipe(
        switchMap(inView => {
          if (!inView) {
            return EMPTY;
          } else {
            return combineLatest([
              this.latestNotificationPreferences$,
              this.panAppState.user$,
              this.stale$,
            ]);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
        map(([notificationPreferences, user, _stale]) => {
          return {
            notificationPreferences,
            userId: user.userId,
            emailAddress: user.email ?? '',
          };
        }),
        filter(
          ({ notificationPreferences }) =>
            !!notificationPreferences &&
            notificationPreferences.loading !== true,
        ),
        tap(({ notificationPreferences, userId, emailAddress }) => {
          const viewModel =
            this.viewModelSubject.value ?? ({} as GroupCustomersPageViewModel);
          viewModel.userId = userId;
          viewModel.email = emailAddress;
          viewModel.customerCount = this.customers.length;
          const notificationType = notificationPreferences.types.find(
            type => type.type === viewModel?.activeTypeId,
          );
          const notificationGroup = notificationType?.groups.find(
            group => group.id === viewModel?.activeGroupId,
          );

          let updatedCustomers: { [groupId: string]: ViewModelCustomer[] } = {};
          notificationType?.groups.forEach(group => {
            let enabledCustomerIds = new Set(
              (group.enabledCustomers || []).map(customer =>
                customer.customerNumber.toString(),
              ),
            );
            viewModel.enabledCustomerCount[group.id] = enabledCustomerIds.size;

            updatedCustomers[group.id] = this.customers
              .filter(
                customer =>
                  !viewModel?.customerFilter ||
                  `${customer.customerName}${customer.customerNumber}`
                    .toLowerCase()
                    .includes(viewModel?.customerFilter.toLowerCase()),
              )
              .map(customer => ({
                customerNumber: customer.customerNumber.toString(),
                divisionNumber: customer.divisionNumber.toString(),
                customerName: customer.customerName,
                isEnabled: enabledCustomerIds.has(
                  customer.customerNumber.toString(),
                ),
              }));
          });

          viewModel.activeGroupName = notificationGroup?.name;

          this.viewModelSubject.next({
            ...viewModel,
            notificationGroupIds: (notificationType?.groups ?? []).map(
              group => ({
                name: group.name,
                id: group.id,
              }),
            ),
            customers: updatedCustomers,
          });
        }),
      )
      .subscribe();

    /**
     *  This subscription updates for changes from the backend ngrx selector as well as
     *  building the initial available customer list.
     *
     *  The subscription only takes the full customers list once as it should r
     *  emain unchanged
     *
     *  The subscription also filters out any changes that do not have a valid NotificationPreferences
     *  object.
     */
    this.pageInViewSubject
      .pipe(
        switchMap((inView: boolean) => {
          if (!inView) {
            return EMPTY;
          } else {
            return combineLatest([
              this.notificationPreferencesStateService.selectNotificationPreferences$(),
              this.customerStoreService.loadCustomers$().pipe(
                filter(customers => customers.length > 0),
                take(1),
              ),
            ]);
          }
        }),
        filter(
          ([notificationPreferences, _customers]: [
            NotificationPreferences,
            Customer[],
          ]) => !!notificationPreferences,
        ),
        takeUntilDestroyed(this.destroyRef),
        tap(
          ([notificationPreferences, customers]: [
            NotificationPreferences,
            Customer[],
          ]) => {
            this.customers = customers;
            this.latestNotificationPreferencesSubject.next(
              notificationPreferences,
            );
            this.staleSubject.next();
          },
        ),
      )
      .subscribe();

    this.toggleSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        buffer(this.toggleSubject.pipe(debounceTime(1000))),
        filter(batched => batched.length > 0),
        withLatestFrom(this.viewModelSubject),
      )
      .subscribe(([batched, viewModel]) => {
        const enabledCustomers: UserPreferencesCustomerInfo[] = [];
        const disabledCustomers: UserPreferencesCustomerInfo[] = [];

        batched.forEach(changedcustomer => {
          const state = this.latestStates.get(changedcustomer.customerNumber);
          if (state === 'enabled') {
            enabledCustomers.push({
              customerNumber: changedcustomer.customerNumber,
              divisionNumber: changedcustomer.divisionNumber,
            });
          } else {
            disabledCustomers.push({
              customerNumber: changedcustomer.customerNumber,
              divisionNumber: changedcustomer.divisionNumber,
            });
          }
        });

        this.latestStates.clear();

        // Process both enabled and disabled customers in a single operation
        const pendingPreferences =
          this.pendingNotificationPreferencesService.getPendingPreference(
            viewModel.activeGroupId,
          );

        const customerUpdates = [
          {
            customers: enabledCustomers,
            options: pendingPreferences
              ? pendingPreferences
              : { email: true, push: false },
          },
          {
            customers: disabledCustomers,
            options: { email: false, push: false },
          },
        ];

        customerUpdates.forEach(({ customers, options }) => {
          if (customers.length > 0) {
            this.pendingNotificationPreferencesService.clearPendingPreference(
              viewModel.activeGroupId,
            );
            this.notificationPreferencesStateService.saveNotificationPreferences(
              this.buildSaveNotificationPreferencesRequest(
                viewModel.activeTypeId,
                viewModel.activeGroupName ?? '',
                viewModel.userId,
                viewModel.email,
                customers,
                [options],
                true,
              ),
            );
          }
        });
      });
  }

  ionViewWillEnter() {
    // Get the notification type from route parameters and initialize state
    firstValueFrom(
      this.route.params.pipe(
        map<Params, { typeId: string; groupId: string }>(params => ({
          typeId:
            params[NotificationPreferencesGroupCustomersPage.TYPE_ID_PARAM],
          groupId:
            params[NotificationPreferencesGroupCustomersPage.GROUP_ID_PARAM],
        })),
      ),
    )
      .then(({ typeId, groupId }: { typeId: string; groupId: string }) => {
        // Set active notification type and load details
        this.notificationPreferencesStateService.setActiveNotificationType(
          typeId,
        );
        this.notificationPreferencesStateService.loadNotificationTypeDetails(
          typeId,
        );

        const breadcrumbs = [
          {
            label:
              NotificationPreferencesGroupCustomersPage.TYPE_ID_BACK_LINK_MAP[
                typeId
              ],
            url: `/desktop/notifications/preferences/type-overview/${typeId}`,
            active: false,
          },
        ];

        this.viewModelSubject.next({
          ...this.viewModelSubject.value,
          activeTypeId: typeId,
          activeGroupId: groupId,
          breadcrumbs,
          enabledCustomerCount: {},
          customers: {},
          sortToggle: true,
        });
      })
      .then(() => {
        this.pageInViewSubject.next(true);
      });
  }

  ionViewWillLeave() {
    // Turn off the pageInViewSubject to stop the subscriptions from running
    this.pageInViewSubject.next(false);
  }

  onSelectAllToggled(isChecked: boolean) {
    const viewModel = this.viewModelSubject.value;

    const pendingPreferences =
      this.pendingNotificationPreferencesService.getPendingPreference(
        viewModel.activeGroupId,
      );
    this.notificationPreferencesStateService.saveNotificationPreferences(
      this.buildSaveNotificationPreferencesRequest(
        viewModel.activeTypeId,
        viewModel.activeGroupName ?? '',
        viewModel.userId,
        viewModel.email,
        viewModel.customers[viewModel.activeGroupId].map(customer => ({
          customerNumber: customer.customerNumber,
          divisionNumber: customer.divisionNumber,
        })),
        isChecked
          ? [pendingPreferences ?? { email: true, push: false }]
          : [{ email: false, push: false }],
        isChecked,
      ),
    );
  }

  onSortClick() {
    this.viewModelSubject.next({
      ...this.viewModelSubject.value,
      sortToggle: !this.viewModelSubject.value.sortToggle,
    });
    this.customers.reverse();
    this.staleSubject.next();
  }

  onGroupSelected(group: { name: string; id: string }) {
    this.viewModelSubject.next({
      ...this.viewModelSubject.value,
      activeGroupId: group.id,
      activeGroupName: group.name,
    });
  }

  trackByCustomer(index: number, customer: any): string {
    return customer.customerNumber;
  }

  onCustomerToggled(customer: Customer, isEnabled: boolean) {
    this.latestStates.set(
      customer.customerNumber.toString(),
      isEnabled ? 'enabled' : 'disabled',
    );
    this.toggleSubject.next({
      customerNumber: customer.customerNumber.toString(),
      divisionNumber: customer.divisionNumber.toString(),
      state: isEnabled ? 'enabled' : 'disabled',
    });
  }

  onSearchInput(value: string) {
    this.viewModelSubject.next({
      ...this.viewModelSubject.value,
      customerFilter: value,
    });
    this.staleSubject.next();
  }

  private buildSaveNotificationPreferencesRequest(
    typeId: string,
    groupId: string,
    userId: string,
    email: string,
    customers: {
      customerNumber: string;
      divisionNumber: string;
    }[],
    notificationOptions: {
      email: boolean;
      push: boolean;
    }[],
    alertEnabled: boolean,
  ): SaveNotificationPreferencesRequest {
    return {
      userId,
      email,
      alerts: [
        {
          alertType: typeId as AlertType,
          alerts: [
            {
              alertName: groupId,
              notificationOptions: notificationOptions,
              customers,
              alertEnabled,
            } as AlertDetail,
          ],
        },
      ],
    };
  }
}
